import React, { useEffect, useState } from "react";
import { api } from "../lib/api";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Trash2, Share2, Heart, GitCompare } from "lucide-react";

const Outfits = () => {
  const [outfits, setOutfits] = useState([]);
  const [items, setItems] = useState({});
  const [tryons, setTryons] = useState({});
  const [compareMode, setCompareMode] = useState(false);
  const [picked, setPicked] = useState([]); // tryon_ids
  const navigate = useNavigate();

  const load = async () => {
    const [o, w, t] = await Promise.all([api.get("/outfits"), api.get("/wardrobe"), api.get("/tryon")]);
    setOutfits(o.data);
    const map = {}; w.data.forEach((i) => map[i.id] = i); setItems(map);
    const tm = {}; t.data.forEach((x) => tm[x.id] = x); setTryons(tm);
  };
  useEffect(() => { load(); }, []);

  const remove = async (id) => {
    if (!confirm("Delete this outfit?")) return;
    await api.delete(`/outfits/${id}`);
    setOutfits((p) => p.filter(x => x.id !== id));
  };

  const togglePublic = async (o) => {
    try {
      const r = await api.patch(`/outfits/${o.id}`, {
        name: o.name, item_ids: o.item_ids, tryon_id: o.tryon_id, notes: o.notes,
        is_public: !o.is_public,
      });
      setOutfits((p) => p.map(x => x.id === o.id ? r.data : x));
      toast.success(r.data.is_public ? "Published to Discover" : "Made private");
    } catch { toast.error("Could not update"); }
  };

  const share = (slug) => {
    const url = `${window.location.origin}/outfits/${slug}`;
    navigator.clipboard.writeText(url);
    toast.success("Share link copied");
  };

  const togglePick = (tryonId) => {
    setPicked((p) => {
      if (p.includes(tryonId)) return p.filter(x => x !== tryonId);
      if (p.length >= 2) return [p[1], tryonId]; // rolling window of 2
      return [...p, tryonId];
    });
  };

  const openCompare = () => {
    if (picked.length !== 2) return toast.error("Pick exactly two try-ons");
    navigate(`/compare?ids=${picked.join(",")}`);
  };

  const exitCompare = () => { setCompareMode(false); setPicked([]); };

  return (
    <div className="max-w-[1400px] mx-auto px-6 sm:px-10 py-12">
      <div className="border-b border-black/10 pb-8 mb-12 flex flex-wrap items-end justify-between gap-4">
        <div>
          <div className="font-mono-eyebrow">Saved compositions</div>
          <h1 className="font-serif-display text-5xl md:text-6xl leading-none mt-2">Outfit Archive</h1>
        </div>
        <div className="flex items-center gap-3">
          {!compareMode ? (
            <button
              data-testid="compare-mode-toggle"
              onClick={() => setCompareMode(true)}
              className="btn-outline-ink inline-flex items-center gap-2"
            >
              <GitCompare className="w-3.5 h-3.5"/> Compare two
            </button>
          ) : (
            <>
              <span data-testid="compare-picked-count" className="font-mono-eyebrow text-black/60">
                {picked.length}/2 selected
              </span>
              <button
                data-testid="compare-open-btn"
                onClick={openCompare}
                disabled={picked.length !== 2}
                className="btn-ink disabled:opacity-40 inline-flex items-center gap-2"
              >
                <GitCompare className="w-3.5 h-3.5"/> Compare
              </button>
              <button
                data-testid="compare-exit-btn"
                onClick={exitCompare}
                className="btn-outline-ink"
              >
                Cancel
              </button>
            </>
          )}
          <Link to="/studio" className="btn-ink hidden md:inline-block">Compose new</Link>
        </div>
      </div>

      {outfits.length === 0 ? (
        <div className="text-center py-20">
          <p className="font-serif-display text-4xl">No outfits archived.</p>
          <p className="text-black/60 mt-2 font-mono-eyebrow">Generate a try-on in the Studio to save your first look.</p>
          <Link to="/studio" className="btn-outline-ink mt-6 inline-block">Enter the studio</Link>
        </div>
      ) : (
        <div className="grid grid-cols-12 gap-8">
          {outfits.map((o, idx) => {
            const tryon = o.tryon_id ? tryons[o.tryon_id] : null;
            const its = o.item_ids.map(id => items[id]).filter(Boolean);
            const isPicked = tryon && picked.includes(tryon.id);
            const canPick = compareMode && tryon; // outfits without a try-on can't be compared
            return (
              <article
                key={o.id}
                data-testid={`outfit-card-${o.id}`}
                onClick={() => canPick && togglePick(tryon.id)}
                className={`col-span-12 md:col-span-6 lg:col-span-4 bg-white border flex flex-col transition-all ${
                  isPicked ? "border-[color:var(--accent)] ring-2 ring-[color:var(--accent)]" :
                  canPick ? "border-black/10 hover:border-black cursor-pointer" : "border-black/10"
                }`}
              >
                <div className="aspect-[3/4] overflow-hidden bg-neutral-100 relative">
                  {tryon ? (
                    <img src={tryon.result_data_url} alt={o.name} className="w-full h-full object-cover"/>
                  ) : (
                    <div className="grid grid-cols-2 h-full">
                      {its.slice(0,4).map((it,i) => (
                        <img key={it.id || i} src={it.image_url} alt="" className="w-full h-full object-cover"/>
                      ))}
                    </div>
                  )}
                  <span className="absolute top-4 left-4 bg-white/90 backdrop-blur px-3 py-1 font-mono-eyebrow">N° {String(outfits.length - idx).padStart(3,"0")}</span>
                  {o.is_public && (
                    <span className="absolute top-4 right-4 bg-[color:var(--accent)] text-white px-3 py-1 font-mono-eyebrow">Public</span>
                  )}
                  {tryon?.is_favorite && (
                    <span
                      data-testid={`fav-badge-${tryon.id}`}
                      className="absolute bottom-4 left-4 bg-black text-white px-3 py-1 font-mono-eyebrow inline-flex items-center gap-1.5"
                    >
                      <Heart className="w-3 h-3 fill-white"/> Winner
                    </span>
                  )}
                  {compareMode && tryon && (
                    <span
                      data-testid={`pick-badge-${tryon.id}`}
                      className={`absolute bottom-4 right-4 w-8 h-8 rounded-full border-2 flex items-center justify-center font-mono-eyebrow text-xs ${
                        isPicked ? "bg-[color:var(--accent)] text-white border-[color:var(--accent)]" : "bg-white/90 border-black/30"
                      }`}
                    >
                      {isPicked ? picked.indexOf(tryon.id) + 1 : ""}
                    </span>
                  )}
                </div>
                <div className="p-6 flex-1 flex flex-col">
                  <div className="font-serif-display text-2xl leading-tight">{o.name}</div>
                  {o.notes && <p className="text-sm text-black/60 mt-2 leading-relaxed line-clamp-2">{o.notes}</p>}
                  <div className="mt-auto pt-6 flex items-center justify-between gap-2">
                    <button
                      onClick={(e) => { e.stopPropagation(); togglePublic(o); }}
                      className="text-xs font-mono-eyebrow underline hover:text-[color:var(--accent)]"
                    >
                      {o.is_public ? "Make private" : "Publish"}
                    </button>
                    <div className="flex gap-2">
                      {o.share_slug && (
                        <button onClick={(e) => { e.stopPropagation(); share(o.share_slug); }} className="p-2 hover:bg-black/5" title="Copy share link">
                          <Share2 className="w-4 h-4"/>
                        </button>
                      )}
                      <button onClick={(e) => { e.stopPropagation(); remove(o.id); }} className="p-2 hover:bg-black/5 text-black/60 hover:text-red-600" title="Delete">
                        <Trash2 className="w-4 h-4"/>
                      </button>
                    </div>
                  </div>
                </div>
              </article>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default Outfits;
